import { ADJUST_LOOKS, AdjustLookId, DEFAULT_ADJUSTMENTS, PREVIEW_MAX_EDGE } from './adjust.types'
import { mergeAdjustments, processPixels } from './adjustColor'

const isLocalUrl = (url: string) => url.startsWith('blob:') || url.startsWith('data:')

/** Loads a photo for canvas use. Remote URLs need CORS (the canvas reads its pixels back), local ones never do. */
export const loadAdjustImage = (url: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const image = new Image()
  if (!isLocalUrl(url)) image.crossOrigin = 'anonymous'
  image.onload = () => resolve(image)
  image.onerror = () => reject(new Error('adjust_image_load_failed'))
  image.src = url
})

/** Like `loadAdjustImage`, but falls back to a non-CORS load: fine for an image only DRAWN on screen (Before) */
export const loadDisplayImage = (url: string): Promise<HTMLImageElement> => loadAdjustImage(url).catch(() => new Promise((resolve, reject) => {
  const image = new Image()
  image.onload = () => resolve(image)
  image.onerror = () => reject(new Error('adjust_image_load_failed'))
  image.src = url
}))

/** Downscales by halving, then one final high-quality step (a single big downscale aliases) */
export const downscaleToCanvas = (image: CanvasImageSource & { width: number, height: number }, sourceWidth: number, sourceHeight: number, maxEdge: number): HTMLCanvasElement => {
  const ratio = Math.min(1, maxEdge / Math.max(sourceWidth, sourceHeight))
  const targetWidth = Math.max(1, Math.round(sourceWidth * ratio))
  const targetHeight = Math.max(1, Math.round(sourceHeight * ratio))
  let current: CanvasImageSource = image
  let width = sourceWidth
  let height = sourceHeight
  while (width / 2 >= targetWidth && height / 2 >= targetHeight) {
    const half = document.createElement('canvas')
    half.width = Math.round(width / 2)
    half.height = Math.round(height / 2)
    const ctx = half.getContext('2d')
    if (!ctx) break
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(current, 0, 0, half.width, half.height)
    current = half
    width = half.width
    height = half.height
  }
  const canvas = document.createElement('canvas')
  canvas.width = targetWidth
  canvas.height = targetHeight
  const ctx = canvas.getContext('2d')
  if (ctx) {
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(current, 0, 0, targetWidth, targetHeight)
  }
  return canvas
}

/** The canvas the live preview renders from: the photo, long edge ≤ PREVIEW_MAX_EDGE */
export const createPreviewSource = (image: HTMLImageElement): HTMLCanvasElement => downscaleToCanvas(image, image.naturalWidth, image.naturalHeight, PREVIEW_MAX_EDGE)

const THUMBNAIL_EDGE = 180

/** A small rendering of every look on the current photo, for the Filters grid (CPU path: tiny images) */
export const createLookThumbnails = (preview: HTMLCanvasElement): Partial<Record<AdjustLookId, string>> => {
  const small = downscaleToCanvas(preview, preview.width, preview.height, THUMBNAIL_EDGE)
  const ctx = small.getContext('2d')
  if (!ctx) return {}
  const base = ctx.getImageData(0, 0, small.width, small.height)
  const thumbnails: Partial<Record<AdjustLookId, string>> = {}
  for (const look of ADJUST_LOOKS) {
    const out = ctx.createImageData(small.width, small.height)
    processPixels(base.data, small.width, small.height, mergeAdjustments({ ...DEFAULT_ADJUSTMENTS }, { id: look.id, intensity: 100 }), out.data)
    ctx.putImageData(out, 0, 0)
    thumbnails[look.id] = small.toDataURL('image/jpeg', 0.8)
  }
  ctx.putImageData(base, 0, 0)
  return thumbnails
}

const LOGO_MAX_EDGE = 1200

/** An uploaded logo, kept at a sensible size (stickers never need more) */
export const readLogoFile = (logo: File): Promise<HTMLCanvasElement> => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(logo)
  const image = new Image()
  image.onload = () => {
    const width = image.naturalWidth || 512
    const height = image.naturalHeight || 512
    resolve(downscaleToCanvas(image, width, height, LOGO_MAX_EDGE))
    URL.revokeObjectURL(url)
  }
  image.onerror = () => {
    URL.revokeObjectURL(url)
    reject(new Error('adjust_logo_load_failed'))
  }
  image.src = url
})
